import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Suspense } from 'react'
import './index.css'
// After index.css so these rules win over Tailwind's preflight.
import './styles/blog-content.css'
import './i18n'
import App from './App.tsx'
import { ToastProvider } from './components/ui/Toast'
import { AuthProvider } from '@/contexts/AuthContext'
import { CreditProvider } from '@/contexts/CreditContext'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import { PageLoader } from './components/ui/page-loader'

/*
 * Recover from stale chunk references after a deploy.
 *
 * Every build gives the lazy() route chunks new hashed names. A tab that was
 * opened before the deploy (or a cached index.html) still asks for the old
 * names, and Cloudflare Pages answers a missing asset with index.html + 200
 * instead of a 404 — so the browser reports "Expected a JavaScript-or-Wasm
 * module script but the server responded with a MIME type of text/html".
 *
 * Vite fires `vite:preloadError` for exactly this failure. Reloading picks up
 * the fresh index.html and the current chunk names. The sessionStorage flag
 * keeps a genuinely broken deploy from turning into a reload loop.
 */
const RELOAD_KEY = 'chunk-reload-at'
const RELOAD_COOLDOWN_MS = 30_000

window.addEventListener('vite:preloadError', (event) => {
  const lastAttempt = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
  if (Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) {
    // Reloading did not help — the chunk is really gone. Let the error surface
    // to the ErrorBoundary rather than spinning in a reload loop.
    return
  }
  event.preventDefault()
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  window.location.reload()
})

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})


createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <AuthProvider>
            <CreditProvider>
              <ToastProvider>
                <App />
              </ToastProvider>
            </CreditProvider>
          </AuthProvider>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>,
)
